import { parseQuery } from '@calendly/http-tools'
import { I18n } from '@calendly/i18n'

import { TYPE_INTERNAL } from './constants'

const TIME_UNITS = { minutes: { toMin: 1, toSec: 60 }, hours: { toMin: 60, toSec: 3600 }, days: { toMin: 1440, toSec: 86400 } }

// i18n-tasks-use t('publisher.event_types.one_off.units.minutes')
// i18n-tasks-use t('publisher.event_types.one_off.units.hours')
// i18n-tasks-use t('publisher.event_types.one_off.units.days')
export const buildTimeUnitOptions = () => Object.keys(TIME_UNITS).map((unit) => ({
  id: unit,
  name: I18n.t(`publisher.event_types.one_off.units.${unit}`)
}))

const durationInMinutes = ({ duration, customDurationValue, customDurationUnit }) => {
  if (duration === 'custom' && customDurationValue && customDurationUnit) {
    return Number(customDurationValue) * TIME_UNITS[customDurationUnit].toMin
  } if (duration) {
    return Number(duration) * TIME_UNITS.minutes.toMin
  }
  throw new Error(
    `Cannot calculate event type duration \\
      duration: ${duration}, \\
      customDurationValue: ${customDurationValue}, \\
      customDurationUnit: ${customDurationUnit}`
  )
}

const formatDoubleBookingEvent = (event) => {
  const { type, eventId, eventUuid, pullCalendarId, calendarAccountUuid } = event

  if (type === TYPE_INTERNAL) {
    return { internal_event_uuid: eventUuid }
  }

  return {
    external_event_id: eventId,
    pull_calendar_id: pullCalendarId,
    calendar_account_uuid: calendarAccountUuid
  }
}

const buildDoubleBookingRules = (selectedSpots) => {
  const filteredDoubleBookingEvents = selectedSpots.map(({ doubleBookingEvents }) => (doubleBookingEvents)).filter(Boolean).flat()

  return filteredDoubleBookingEvents.map((event) => formatDoubleBookingEvent(event))
}

const locationPayload = ({
  location,
  hideLocation,
  customLocation,
  phoneNumber,
  phoneDetails,
  physicalAddress,
  physicalDetails,
  memberships
}) => {
  const locationType = { location_type: location }
  const locationDetails = () => {
    switch (location) {
      case 'physical':
        return ({ location: physicalAddress, additional_info: physicalDetails })
      case 'inbound_call':
        return ({ phone_number: phoneNumber, additional_info: phoneDetails })
      case 'custom':
        return ({ location: customLocation, hide_location: hideLocation })
      case 'google_conference':
      case 'microsoft_teams_conference':
        return ({ location_organizer_user_id: null })
      default:
        return { location_organizer_user_id: memberships.find((m) => m.location_organizer)?.user_id }
    }
  }

  return { ...locationType, ...locationDetails() }
}

const appParams = () => {
  const allParams = parseQuery(window.location.search)
  if (
    allParams.platform
    && allParams.version
    && allParams.source) {
    return {
      app_platform: allParams.platform,
      app_version: allParams.version,
      app_source: allParams.source
    }
  }
  return {}
}

const notPrioritySpots = (spots) => (
  spots
    .map(({ start, end }) => ({ start, end }))
    .map((spot) => ({ ...spot, high_priority: false }))
)

export const buildSubmitFormPayload = ({
  eventType: {
    name,
    details: description,
    location,
    clearLocation,
    inviteeLocale,
    physicalAddress,
    physicalDetails,
    customLocation,
    phoneNumber,
    phoneDetails,
    duration,
    customDurationValue,
    customDurationUnit,
    memberships = [],
    hideLocation = false,
  },
  bookingMethod,
  selectedSpots,
  timezone,
  reserveTimes,
  notifyInvitees,
  votesVisible
}) => ({
  name,
  description,
  ...appParams(),
  ...locationPayload({
    location,
    hideLocation,
    customLocation,
    phoneNumber,
    phoneDetails,
    physicalAddress,
    physicalDetails,
    memberships
  }),
  clear_location: clearLocation,
  duration: durationInMinutes({ duration, customDurationValue, customDurationUnit }),
  selected_spots: notPrioritySpots(selectedSpots),
  double_booking_rules: buildDoubleBookingRules(selectedSpots),
  booking_method: bookingMethod,
  include_owner_membership: false,
  locale: inviteeLocale,
  timezone,
  lock_timezone_enabled: location === 'physical',
  reserve_times: reserveTimes,
  votes_visible: votesVisible,
  notify_invitees: notifyInvitees,
  memberships: memberships.map((m) => ({ user_uuid: m.user_uuid, location_organizer: !!m.location_organizer }))
})
